import { useCallback, useEffect, useState } from "react";
import { InlineHelp } from "@/components/common/InlineHelp";
import { parseGoogleDriveResourceId } from "@/lib/ops/googleDriveResourceId";
import { patchOpsSettings } from "@/lib/opsPaperworkClient";
import { OpsDriveFolderRecipeEditor } from "./OpsDriveFolderRecipeEditor";
import {
  clearOpsGoogleDriveSecret,
  getOpsGoogleDriveSecret,
  postOpsGoogleDriveBuildFolders,
  postOpsGoogleDriveResortFolders,
  postOpsGoogleDriveSecret,
  type OpsDriveSecretStatus,
} from "@/lib/opsPaperworkClientDrive";

function stopDetailsToggle(e: { stopPropagation: () => void }) {
  e.stopPropagation();
}

export function OpsPaperworkDriveSettingsSection(props: {
  settings: Record<string, unknown>;
  setSettings: (s: Record<string, unknown>) => void;
  onToggleSetting: (key: string, value: boolean) => Promise<void>;
  setMsg: (s: string) => void;
}) {
  const [secret, setSecret] = useState<Omit<OpsDriveSecretStatus, "ok"> | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [folderBusy, setFolderBusy] = useState(false);
  const [rootUrl, setRootUrl] = useState(
    typeof props.settings.google_drive_root_url === "string" ? props.settings.google_drive_root_url : ""
  );
  const [template, setTemplate] = useState(
    typeof props.settings.google_drive_folder_template === "string"
      ? props.settings.google_drive_folder_template
      : ""
  );

  useEffect(() => {
    setRootUrl(typeof props.settings.google_drive_root_url === "string" ? props.settings.google_drive_root_url : "");
  }, [props.settings.google_drive_root_url]);
  useEffect(() => {
    setTemplate(
      typeof props.settings.google_drive_folder_template === "string"
        ? props.settings.google_drive_folder_template
        : ""
    );
  }, [props.settings.google_drive_folder_template]);

  const parsedRootId = parseGoogleDriveResourceId(rootUrl);
  const enabled = Boolean(props.settings.google_workspace_drive_enabled);

  const loadSecret = useCallback(async () => {
    const r = await getOpsGoogleDriveSecret();
    if (r.ok) {
      setSecret({
        configured: r.data.configured,
        client_email: r.data.client_email,
        updated_at: r.data.updated_at,
        encryption_configured: r.data.encryption_configured,
        env_fallback: r.data.env_fallback,
      });
    }
  }, []);

  useEffect(() => {
    void loadSecret();
  }, [loadSecret]);

  const nested = props.settings.google_drive_secret;
  const nestedStatus =
    nested && typeof nested === "object" && !Array.isArray(nested)
      ? (nested as Omit<OpsDriveSecretStatus, "ok">)
      : null;
  const status = secret ?? nestedStatus;
  const email = status?.client_email ?? null;

  async function saveFolderSettings(opts?: { stayBusy?: boolean }): Promise<boolean> {
    if (!opts?.stayBusy) setFolderBusy(true);
    const url = rootUrl.trim() || null;
    const r = await patchOpsSettings({
      google_drive_root_url: url,
      google_drive_folder_template: template.trim() || null,
    });
    if (!opts?.stayBusy) setFolderBusy(false);
    if (!r.ok) {
      props.setMsg(r.error);
      return false;
    }
    const next = (r.data as { settings: Record<string, unknown> }).settings ?? {};
    props.setSettings({ ...props.settings, ...next });
    const savedId =
      typeof next.google_drive_root_id === "string" ? next.google_drive_root_id : parsedRootId;
    if (url && !savedId) {
      props.setMsg("Saved the URL text, but could not parse a Drive folder id. Paste a folder/Shared Drive URL or the id.");
      return false;
    }
    if (!opts?.stayBusy) {
      props.setMsg(savedId ? `Saved Drive root (${savedId}).` : "Cleared Drive root folder.");
    }
    return true;
  }

  async function buildFolderStructure() {
    setFolderBusy(true);
    const saved = await saveFolderSettings({ stayBusy: true });
    if (!saved) {
      setFolderBusy(false);
      return;
    }
    const r = await postOpsGoogleDriveBuildFolders(template);
    setFolderBusy(false);
    if (!r.ok) {
      props.setMsg(r.error);
      return;
    }
    props.setMsg(r.data.message);
  }

  async function previewResort() {
    setFolderBusy(true);
    const r = await postOpsGoogleDriveResortFolders(template, true);
    setFolderBusy(false);
    if (!r.ok) {
      props.setMsg(r.error);
      return;
    }
    const sample = (r.data.samples ?? [])
      .filter((s) => s.path)
      .slice(0, 3)
      .map((s) => `${s.display_name || s.deal_id} → ${s.path}`)
      .join("; ");
    props.setMsg(sample ? `${r.data.message} ${sample}` : r.data.message);
  }

  async function applyResort() {
    setFolderBusy(true);
    const saved = await saveFolderSettings({ stayBusy: true });
    if (!saved) {
      setFolderBusy(false);
      return;
    }
    const r = await postOpsGoogleDriveResortFolders(template, false);
    setFolderBusy(false);
    if (!r.ok) {
      props.setMsg(r.error);
      return;
    }
    props.setMsg(r.data.message);
  }

  async function saveJson() {
    setBusy(true);
    const r = await postOpsGoogleDriveSecret(draft);
    setBusy(false);
    if (!r.ok) {
      props.setMsg(r.error);
      return;
    }
    setDraft("");
    props.setMsg(`Saved Drive service account${r.data.client_email ? ` (${r.data.client_email})` : ""}.`);
    await loadSecret();
  }

  async function clearJson() {
    setBusy(true);
    const r = await clearOpsGoogleDriveSecret();
    setBusy(false);
    if (!r.ok) {
      props.setMsg(r.error);
      return;
    }
    props.setMsg("Cleared this org’s Drive service account. Env fallback applies if set.");
    await loadSecret();
  }

  return (
    <div className="relative z-10 space-y-2" onClick={stopDetailsToggle} onPointerDown={stopDetailsToggle}>
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => void props.onToggleSetting("google_workspace_drive_enabled", e.target.checked)}
          />
          Mirror paperwork into this org’s Shared Drive
        </label>
        <InlineHelp label="How Drive folders work">
          Tokens in any folder name: {"{year}"} {"{month}"} {"{customer}"} {"{order}"} {"{display_name}"} {"{deal_id_short}"}{" "}
          {"{serial}"} {"{office}"} {"{slot}"}. Empty segments are skipped. Build creates the shared branches
          (Pending / Cancelled / Delivered, this year’s dates) under the Drive root. Per-deal folders still appear
          when paperwork is mirrored. Existing names are reused. Re-sort moves already-mirrored deal folders into
          the current recipe (40 per click; skips deals that have never been mirrored). Empty parent folders are
          left in place. Share that Drive with the org service account.
        </InlineHelp>
      </div>
      <label className="block text-xs text-gray-600">
        Shared Drive / root folder URL or id
        <input
          className="mt-1 block w-full rounded border border-gray-300 px-2 py-1 font-mono text-sm"
          placeholder="https://drive.google.com/drive/folders/…"
          value={rootUrl}
          onChange={(e) => setRootUrl(e.target.value)}
        />
      </label>
      <p className="text-[11px] text-gray-500">
        {rootUrl.trim()
          ? parsedRootId
            ? `Parsed folder id: ${parsedRootId}`
            : "Could not parse a folder id from this value yet."
          : "Paste the folder URL, a Shared Drive URL, or the raw id."}
      </p>
      <OpsDriveFolderRecipeEditor value={template} onChange={setTemplate} />
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="rounded border px-2 py-1 text-xs"
          disabled={folderBusy}
          onClick={() => void saveFolderSettings()}
        >
          {folderBusy ? "Working…" : "Save folder settings"}
        </button>
        <button
          type="button"
          className="rounded border px-2 py-1 text-xs"
          disabled={folderBusy}
          onClick={() => void buildFolderStructure()}
        >
          Build folder structure
        </button>
        <button
          type="button"
          className="rounded border px-2 py-1 text-xs"
          disabled={folderBusy}
          onClick={() => void previewResort()}
        >
          Preview re-sort
        </button>
        <button
          type="button"
          className="rounded border px-2 py-1 text-xs"
          disabled={folderBusy}
          onClick={() => void applyResort()}
        >
          Re-sort deal folders
        </button>
      </div>
      <p className="text-xs text-gray-600">
        Share that Drive with{" "}
        <code className="rounded bg-gray-100 px-1 text-[11px]">{email || "the org service account (save JSON below)"}</code>
        {status?.env_fallback ? " (env fallback)" : null}.
      </p>
      <p className="text-[11px] text-gray-500">
        {status?.configured
          ? `Org service account saved${status.updated_at ? ` ${new Date(status.updated_at).toLocaleString()}` : ""}.`
          : "No org service account saved yet."}
        {status && !status.encryption_configured ? " Server encryption key missing." : ""}
      </p>
      <details>
        <summary className="cursor-pointer text-xs font-semibold text-indigo-900">Save Google service account JSON</summary>
        <textarea
          className="mt-1 w-full min-h-[4.5rem] rounded border border-gray-300 px-2 py-1 font-mono text-[11px]"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder='{"type":"service_account","client_email":"…","private_key":"…"}'
          disabled={busy || status?.encryption_configured === false}
        />
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded border px-2 py-1 text-xs"
            disabled={busy || !draft.trim()}
            onClick={() => void saveJson()}
          >
            {busy ? "Saving…" : "Save for this org"}
          </button>
          <button
            type="button"
            className="rounded border px-2 py-1 text-xs"
            disabled={busy || !status?.configured}
            onClick={() => void clearJson()}
          >
            Clear saved
          </button>
        </div>
      </details>
    </div>
  );
}
